/**
 * Teknik detay sayfasında "Pratik Yap" bölümünü oluşturan bileşen.
 *
 * - Solver-doğrulamalı 9x9 puzzle önizlemesi gösterir
 * - 2 CTA: "Tam Oyna" → /oyna?puzzle=...  ve  "Çözücüde Aç" → /cozucu?puzzle=...
 * - Üretilmiş puzzle yoksa null döner (parent bileşeni hiç göstermez)
 */

import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useRoutes } from '../hooks/useRoutes';
import { getTechniquePuzzle } from '../data/techniquePuzzles';
import './TechniquePractice.css';

interface Props {
  slug: string;
}

export function TechniquePractice({ slug }: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { r } = useRoutes();
  const data = getTechniquePuzzle(slug);

  if (!data) return null;

  const cells: (number | null)[] = [];
  for (let i = 0; i < 81; i++) {
    const ch = data.puzzle[i];
    cells.push(ch && ch !== '0' && ch !== '.' ? parseInt(ch, 10) : null);
  }

  const startGame = () => {
    const url = `${r('play')}?puzzle=${data.puzzle}&solution=${data.solution}&difficulty=${data.difficulty}`;
    navigate(url);
  };

  const openInSolver = () => {
    // App.tsx (solver) ?board=row1;row2;... formatı bekliyor (her satır virgüllü 9 değer, 0=boş)
    const rows: string[] = [];
    for (let r = 0; r < 9; r++) {
      const row: string[] = [];
      for (let c = 0; c < 9; c++) {
        const ch = data.puzzle[r * 9 + c];
        row.push(ch === '.' || ch === '0' ? '0' : ch);
      }
      rows.push(row.join(','));
    }
    const boardParam = encodeURIComponent(rows.join(';'));
    navigate(`${r('solver')}?board=${boardParam}`);
  };

  const tp = t.techPractice as any;
  const heading = tp.heading;
  const sub = tp.sub;
  const diff = (t.difficulties as any)[data.difficulty] ?? `D${data.difficulty}`;
  const givensLabel = tp.givensLabel;
  const strictModeLabel = tp.strictModeLabel;
  const playBtn = tp.playBtn;
  const solverBtn = tp.solverBtn;

  return (
    <div className="tech-practice">
      <h2>{heading}</h2>
      <p className="tech-practice-sub">{sub}</p>

      <div className="tech-practice-meta">
        <span className="tech-practice-chip">{diff}</span>
        <span className="tech-practice-chip">{data.givens} {givensLabel}</span>
        {data.mode === 'strict' && (
          <span className="tech-practice-chip tech-practice-chip--mode">{strictModeLabel}</span>
        )}
      </div>

      <div className="tech-practice-board" role="img" aria-label={heading}>
        {cells.map((v, i) => {
          const r9 = Math.floor(i / 9);
          const c9 = i % 9;
          const cls = [
            'tp-cell',
            v != null ? 'tp-given' : 'tp-empty',
            c9 % 3 === 2 && c9 < 8 ? 'tp-br' : '',
            r9 % 3 === 2 && r9 < 8 ? 'tp-bb' : '',
          ].filter(Boolean).join(' ');
          return (
            <div key={i} className={cls}>
              {v ?? ''}
            </div>
          );
        })}
      </div>

      <div className="tech-practice-actions">
        <button className="tech-practice-btn tech-practice-btn--primary" onClick={startGame}>
          {playBtn}
        </button>
        <button className="tech-practice-btn" onClick={openInSolver}>
          {solverBtn}
        </button>
      </div>
    </div>
  );
}
